<template>
  <section class="sr-card" data-testid="signal-rules-card">
    <header class="card-header">
      <h2 class="card-title">Signal rules</h2>
      <p class="card-subtitle">
        When a signal fires on an account’s Adoption tab. Turn a rule off to stop it firing for
        every account.
      </p>
    </header>

    <div class="sr-table" role="table" aria-label="Signal rules">
      <div class="sr-row sr-row--header" role="row">
        <span class="sr-cell sr-cell--signal" role="columnheader">Signal</span>
        <span class="sr-cell sr-cell--threshold" role="columnheader">Threshold</span>
        <span class="sr-cell sr-cell--severity" role="columnheader">Severity</span>
        <span class="sr-cell sr-cell--active" role="columnheader">Active</span>
        <span class="sr-cell sr-cell--playbook" role="columnheader">Playbook</span>
      </div>

      <div
        v-for="rule in rules"
        :key="rule.key"
        class="sr-row"
        role="row"
        :data-testid="`signal-rule-${rule.key}`"
      >
        <span class="sr-cell sr-cell--signal" role="cell">
          {{ rule.label }}
        </span>

        <span class="sr-cell sr-cell--threshold" role="cell">
          <template v-if="rule.hasThreshold">
            <v-text-field
              :value="rule.threshold"
              :disabled="readOnly"
              type="number"
              min="0"
              hide-details
              dense
              outlined
              class="sr-threshold-input"
              :aria-label="`${rule.label} threshold`"
              :data-testid="`signal-threshold-${rule.key}`"
              @input="onThresholdInput(rule, $event)"
            />
            <span class="sr-unit" :data-testid="`signal-unit-${rule.key}`">{{ rule.unit }}</span>
          </template>
          <span v-else class="sr-no-threshold" data-testid="signal-no-threshold">
            No threshold
          </span>
        </span>

        <span class="sr-cell sr-cell--severity" role="cell">
          <span
            class="severity-badge"
            :style="severityStyle(rule.severity)"
            :data-testid="`signal-severity-${rule.key}`"
          >
            {{ severityLabel(rule.severity) }}
          </span>
        </span>

        <span class="sr-cell sr-cell--active" role="cell">
          <v-switch
            :input-value="rule.active"
            :disabled="readOnly"
            hide-details
            dense
            class="sr-toggle"
            :aria-label="`${rule.label} active`"
            :data-testid="`signal-toggle-${rule.key}`"
            @change="onToggle(rule, $event)"
          />
        </span>

        <span class="sr-cell sr-cell--playbook" role="cell">
          <button
            type="button"
            class="sr-playbook-btn"
            :aria-expanded="openPlaybook === rule.key ? 'true' : 'false'"
            :data-testid="`signal-playbook-${rule.key}`"
            @click="togglePlaybook(rule.key)"
          >
            {{ rule.playbookUrl ? "Playbook" : "Add" }}
          </button>
        </span>
      </div>

      <div v-if="openPlaybook" class="sr-playbook-panel" data-testid="signal-playbook-panel">
        <v-text-field
          :value="openPlaybookRule.playbookUrl || ''"
          :disabled="readOnly"
          label="Playbook URL"
          hide-details
          dense
          outlined
          data-testid="signal-playbook-input"
          @input="onPlaybookInput(openPlaybookRule, $event)"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref } from "vue";

const props = defineProps({
  rules: {
    type: Array,
    default: () => [],
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:rules"]);

const SEVERITY_META = {
  critical: { label: "Critical", text: "#b42318", bg: "#fef3f2", border: "#fecdca" },
  warning: { label: "Warning", text: "#b54708", bg: "#fffaeb", border: "#fedf89" },
  positive: { label: "Positive", text: "#067647", bg: "#ecfdf3", border: "#abefc6" },
};

const openPlaybook = ref(null);

const openPlaybookRule = computed(
  () => props.rules.find((r) => r.key === openPlaybook.value) || null
);

function severityLabel(severity) {
  return SEVERITY_META[severity]?.label || severity;
}

function severityStyle(severity) {
  const meta = SEVERITY_META[severity] || SEVERITY_META.warning;
  return {
    color: meta.text,
    background: meta.bg,
    borderColor: meta.border,
  };
}

function replaceRule(updated) {
  return props.rules.map((r) => (r.key === updated.key ? updated : r));
}

function onThresholdInput(rule, value) {
  const parsed = value === "" || value === null ? "" : Number(value);
  emit("update:rules", replaceRule({ ...rule, threshold: parsed }));
}

function onToggle(rule, value) {
  emit("update:rules", replaceRule({ ...rule, active: Boolean(value) }));
}

function onPlaybookInput(rule, value) {
  emit("update:rules", replaceRule({ ...rule, playbookUrl: value || "" }));
}

function togglePlaybook(key) {
  openPlaybook.value = openPlaybook.value === key ? null : key;
}
</script>

<style scoped>
.sr-card {
  width: 100%;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  padding: 24px;
  box-sizing: border-box;
}

.card-header {
  margin-bottom: 16px;
}

.card-title {
  margin: 0 0 4px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.card-subtitle {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.sr-table {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.sr-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #eaecf0;
}

.sr-row--header {
  border-bottom: 1px solid #d0d5dd;
  padding: 8px 0;
}

.sr-cell {
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}

.sr-cell--signal {
  flex: 1 1 30%;
  font-weight: 600;
  color: #101828;
}

.sr-cell--threshold {
  flex: 1 1 30%;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sr-cell--severity {
  flex: 0 0 90px;
}

.sr-cell--active {
  flex: 0 0 64px;
  display: flex;
  justify-content: center;
}

.sr-cell--playbook {
  flex: 0 0 80px;
  text-align: right;
}

.sr-row--header .sr-cell {
  font-size: 12px;
  font-weight: 500;
  color: #475467;
}

.sr-threshold-input {
  max-width: 96px;
  margin: 0;
}

.sr-unit {
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}

.sr-no-threshold {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.severity-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.sr-playbook-btn {
  border: none;
  background: transparent;
  padding: 4px 8px;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #7f56d9;
}

.sr-playbook-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
  border-radius: 6px;
}

.sr-playbook-panel {
  padding: 8px 0 16px;
  max-width: 400px;
}
</style>
